import useQueryErrorHandler from 'core/hooks/useQueryErrorHandler';
import { QueryStatusData } from 'core/hooks/useQueryHelper';
import { AxiosCreateRequestProps, ServiceData, ServiceRequestResponse } from '../../../types/serviceRequest';
import useAxiosMakeServiceRequest from './useAxiosMakeServiceRequest';
import { useMutation } from '@apollo/client';
import { mutationCreateServiceRequest } from './gql/mutationCreateServiceRequest';
import { MESSAGING } from 'core/datasets/action';
import { useDispatch } from 'react-redux';
import { ConfirmationCallback } from '../../../../AI/types/conversation_callback';
import useAxiosSendAssistantConfirmation from '../../../../AI/useAxiosSendAssistantConfirmation';
import { useTranslation } from 'react-i18next';
import useAxiosSendMessage from 'pages/MessagingChannel/useAxiosSendMessage';

const useMutationMakeServiceRequest = (
    confirmationCallback: ConfirmationCallback | null = null,
): ServiceRequestReturnType => {
    const dispatch = useDispatch();
    const [makeServiceRequest, { data, error, loading, called }] = useMutation(mutationCreateServiceRequest);
    const { errorHandler } = useQueryErrorHandler();
    const { sendConfirmation } = useAxiosSendAssistantConfirmation();
    const { t } = useTranslation();

    const { createRequest, axiosError, axiosData, axiosLoading, uploading } = useAxiosMakeServiceRequest();
    const axiosSendMessage = useAxiosSendMessage();

    const createRequestWithUpdateChannels: typeof createRequest = async (...data) => {
        await createRequest(...data);
        dispatch({ type: MESSAGING.GET_CHANNELS_MAP });
    };

    const createServiceRequest = (serviceData: ServiceData) => {
        makeServiceRequest({ variables: { serviceData } })
            .then(() => dispatch({ type: MESSAGING.GET_CHANNELS_MAP }))
            .catch(errorHandler);
    };

    const createServiceRequestWithoutUpdateChannels: typeof createRequest = async (...data) => {
        await createRequest(...data);

        if (confirmationCallback) {
            await axiosSendMessage.createRequest({ files: [] }, false, t('AI.Yes, create service request'));
            await sendConfirmation(
                confirmationCallback.id,
                confirmationCallback.callbackType,
                confirmationCallback.confirmation,
            );
        }
    };

    return {
        axiosData,
        data: axiosData || (data as any),
        error: axiosError || error,
        loading: axiosLoading || loading,
        called,
        createServiceRequest,
        createRequest: createRequestWithUpdateChannels,
        uploading,
        createServiceRequestWithoutUpdateChannels,
    };
};

export default useMutationMakeServiceRequest;

type ServiceRequestReturnType = QueryStatusData & {
    axiosData: ServiceRequestResponse | null;
    createServiceRequest: (serviceData: ServiceData) => void;
    data: ServiceRequestResponse;
    called: boolean;
    createRequest: (props: AxiosCreateRequestProps) => void;
    uploading: number;
    createServiceRequestWithoutUpdateChannels: (props: AxiosCreateRequestProps) => void;
};
